import { useState } from 'react';

export interface StoreNote {
  id: number;
  storeId: number;
  authorName: string;
  authorId: number;
  content: string;
  createdAt: string;
  isPinned: boolean;
}

interface StoreNotesProps {
  storeId: number;
  notes: StoreNote[];
  onAddNote: (content: string) => Promise<void>;
  isLoading?: boolean;
  disabled?: boolean;
}

export default function StoreNotes({
  notes,
  onAddNote,
  isLoading = false,
  disabled = false,
}: StoreNotesProps) {
  const [newNote, setNewNote] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);

  // Sort notes: pinned first, then by date descending
  const sortedNotes = [...notes].sort((a, b) => {
    if (a.isPinned !== b.isPinned) return a.isPinned ? -1 : 1;
    return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newNote.trim() || isSubmitting || disabled) return;

    setIsSubmitting(true);
    try {
      await onAddNote(newNote.trim());
      setNewNote('');
    } catch (error) {
      console.error('Failed to add note:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffDays === 0) {
      return date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
    } else if (diffDays === 1) {
      return 'Yesterday';
    } else if (diffDays < 7) {
      return date.toLocaleDateString('en-US', { weekday: 'short' });
    } else {
      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    }
  };

  const visibleNotes = isExpanded ? sortedNotes : sortedNotes.slice(0, 3);

  return (
    <div className="rounded-xl bg-white p-4 shadow-card">
      <div className="mb-3 flex items-center justify-between">
        <h3 className="font-heading text-sm font-medium uppercase tracking-wide text-slate-500">
          Store Notes
        </h3>
        {notes.length > 0 && (
          <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-600">
            {notes.length}
          </span>
        )}
      </div>

      {/* Existing Notes */}
      {isLoading ? (
        <div className="flex items-center justify-center py-4">
          <div className="h-5 w-5 animate-spin rounded-full border-2 border-primary-600 border-t-transparent"></div>
        </div>
      ) : sortedNotes.length === 0 ? (
        <div className="py-4 text-center text-sm text-slate-400">
          No notes yet. Be the first to add one!
        </div>
      ) : (
        <div className="mb-3 space-y-2">
          {visibleNotes.map((note) => (
            <div
              key={note.id}
              className={`rounded-lg p-3 ${
                note.isPinned ? 'border border-amber-200 bg-amber-50' : 'bg-slate-50'
              }`}
            >
              <div className="flex items-start justify-between gap-2">
                <div className="flex-1">
                  <p className="text-sm text-slate-700">{note.content}</p>
                  <div className="mt-1 flex items-center gap-2 text-xs text-slate-400">
                    <span className="font-medium text-slate-500">{note.authorName}</span>
                    <span>-</span>
                    <span>{formatDate(note.createdAt)}</span>
                  </div>
                </div>
                {note.isPinned && (
                  <svg className="h-4 w-4 flex-shrink-0 text-amber-500" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M10.707 2.293a1 1 0 00-1.414 0l-7 7a1 1 0 001.414 1.414L4 10.414V17a1 1 0 001 1h2a1 1 0 001-1v-2a1 1 0 011-1h2a1 1 0 011 1v2a1 1 0 001 1h2a1 1 0 001-1v-6.586l.293.293a1 1 0 001.414-1.414l-7-7z" />
                  </svg>
                )}
              </div>
            </div>
          ))}

          {sortedNotes.length > 3 && (
            <button
              onClick={() => setIsExpanded(!isExpanded)}
              className="w-full py-2 text-center text-sm font-medium text-primary-600 hover:text-primary-700"
            >
              {isExpanded ? 'Show less' : `Show ${sortedNotes.length - 3} more`}
            </button>
          )}
        </div>
      )}

      {/* Add Note Form */}
      <form onSubmit={handleSubmit} className="flex gap-2">
        <input
          type="text"
          value={newNote}
          onChange={(e) => setNewNote(e.target.value)}
          placeholder="Add a note for future visits..."
          disabled={disabled || isSubmitting}
          className="flex-1 rounded-lg border border-slate-200 px-3 py-2 text-sm placeholder-slate-400 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500 disabled:bg-slate-50"
          maxLength={500}
        />
        <button
          type="submit"
          disabled={!newNote.trim() || disabled || isSubmitting}
          className="flex items-center justify-center rounded-lg bg-primary-600 px-4 py-2 text-white transition-colors hover:bg-primary-700 disabled:bg-slate-300"
        >
          {isSubmitting ? (
            <svg className="h-5 w-5 animate-spin" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
            </svg>
          ) : (
            <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
          )}
        </button>
      </form>
    </div>
  );
}
